import { useEffect, useState } from 'react'
import { useInvoices } from '@/hooks/useInvoices'
import {FileText, Trash2, Building2, Search, Filter, SquareCheck as CheckSquare, Square, Download} from 'lucide-react'

export default function InvoiceHistoryPage() {
  const { invoices, loading, fetchInvoices, deleteInvoice, bulkDeleteInvoices, bulkUpdateStatus } = useInvoices()
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [currencyFilter, setCurrencyFilter] = useState<string>('all')
  const [dateFilter, setDateFilter] = useState<string>('all')
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([])

  useEffect(() => {
    fetchInvoices({ sort: { createdAt: -1 } })
  }, [])

  // Filter invoices based on search and filters
  const filteredInvoices = invoices.filter((invoice) => {
    const matchesSearch = searchQuery === '' ||
      invoice.invoiceNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.businessName.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter
    const matchesCurrency = currencyFilter === 'all' || invoice.currency === currencyFilter
    
    let matchesDate = true
    if (dateFilter !== 'all') {
      const invoiceDate = new Date(invoice.createdAt)
      const today = new Date()
      const daysDiff = Math.floor((today.getTime() - invoiceDate.getTime()) / (1000 * 60 * 60 * 24))
      
      switch (dateFilter) {
        case 'today':
          matchesDate = daysDiff === 0
          break
        case 'week':
          matchesDate = daysDiff <= 7
          break
        case 'month':
          matchesDate = daysDiff <= 30
          break
        case 'year':
          matchesDate = daysDiff <= 365
          break
      }
    }
    
    return matchesSearch && matchesStatus && matchesCurrency && matchesDate
  })

  const clearFilters = () => {
    setSearchQuery('')
    setStatusFilter('all')
    setCurrencyFilter('all')
    setDateFilter('all')
  }

  const handleDelete = async (invoiceId: string) => {
    if (confirm('Are you sure you want to delete this invoice?')) {
      try {
        await deleteInvoice(invoiceId)
        alert('Invoice deleted successfully')
      } catch (error) {
        alert('Failed to delete invoice')
      }
    }
  }

  const toggleSelectInvoice = (invoiceId: string) => {
    setSelectedInvoices(prev => 
      prev.includes(invoiceId) 
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    )
  }

  const toggleSelectAll = () => {
    if (selectedInvoices.length === filteredInvoices.length) {
      setSelectedInvoices([])
    } else {
      setSelectedInvoices(filteredInvoices.map(inv => inv._id))
    }
  }

  const handleBulkDelete = async () => {
    if (confirm(`Are you sure you want to delete ${selectedInvoices.length} invoice(s)?`)) {
      try {
        await bulkDeleteInvoices(selectedInvoices)
        setSelectedInvoices([])
        alert('Invoices deleted successfully')
      } catch (error) {
        alert('Failed to delete invoices')
      }
    }
  }

  const handleBulkStatusUpdate = async (status: 'draft' | 'sent' | 'paid' | 'pending_payment') => {
    try {
      await bulkUpdateStatus(selectedInvoices, status)
      setSelectedInvoices([])
      alert(`${selectedInvoices.length} invoice(s) marked as ${status.replace('_', ' ')}`)
    } catch (error) {
      alert('Failed to update invoice status')
    }
  }

  const handleBulkExport = () => {
    const selectedData = invoices.filter(inv => selectedInvoices.includes(inv._id))
    const csvContent = [
      ['Invoice #', 'Client', 'Business', 'Date', 'Amount', 'Currency', 'Status'].join(','),
      ...selectedData.map(inv => 
        [inv.invoiceNumber, inv.clientName, inv.businessName, inv.invoiceDate, inv.totalAmount, inv.currency, inv.status].join(',')
      )
    ].join('\n')
    
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `invoices-export-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const getStatusBadge = (status: string) => {
    const styles = {
      paid: 'bg-green-100 text-green-800',
      sent: 'bg-blue-100 text-blue-800',
      pending_payment: 'bg-yellow-100 text-yellow-800',
      draft: 'bg-gray-100 text-gray-800'
    }
    return styles[status as keyof typeof styles] || styles.draft
  }

  const getCurrencySymbol = (currency: string) => {
    const symbols: Record<string, string> = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      CAD: 'C$',
      AUD: 'A$'
    }
    return symbols[currency] || '$'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 py-12 px-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B5394] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading invoices...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Invoice History</h1>
            <div className="text-sm text-gray-600">
              {selectedInvoices.length > 0 ? (
                <span className="font-semibold text-[#0B5394]">{selectedInvoices.length} selected</span>
              ) : (
                <>
                  Showing: <span className="font-semibold">{filteredInvoices.length}</span> of <span className="font-semibold">{invoices.length}</span> invoices
                </>
              )}
            </div>
          </div>
          
          {/* Bulk Actions Bar */}
          {selectedInvoices.length > 0 && (
            <div className="bg-[#0B5394] text-white rounded-lg p-4 mb-6 flex items-center justify-between shadow-lg">
              <div className="flex items-center gap-2">
                <CheckSquare className="w-5 h-5" />
                <span className="font-medium">{selectedInvoices.length} invoice(s) selected</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleBulkStatusUpdate('sent')}
                  className="px-4 py-2 bg-white text-[#0B5394] rounded hover:bg-gray-100 transition-colors text-sm font-medium"
                >
                  Mark as Sent
                </button>
                <button
                  onClick={() => handleBulkStatusUpdate('paid')}
                  className="px-4 py-2 bg-[#047857] text-white rounded hover:bg-[#059669] transition-colors text-sm font-medium"
                >
                  Mark as Paid
                </button>
                <button
                  onClick={handleBulkExport}
                  className="px-4 py-2 bg-white text-[#0B5394] rounded hover:bg-gray-100 transition-colors text-sm font-medium flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
                <button
                  onClick={handleBulkDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm font-medium flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          )}
          
          {/* Search and Filter Bar */}
          <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-6 mb-6 border border-blue-100">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Search Input */}
              <div className="lg:col-span-2 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by invoice #, client, or business..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5394] focus:border-transparent transition-all"
                />
              </div>
              
              {/* Status Filter */}
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5394] focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
                >
                  <option value="all">All Status</option>
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="pending_payment">Pending Payment</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              
              {/* Currency Filter */}
              <div>
                <select
                  value={currencyFilter}
                  onChange={(e) => setCurrencyFilter(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5394] focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
                >
                  <option value="all">All Currencies</option>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                  <option value="CAD">CAD (C$)</option>
                  <option value="AUD">AUD (A$)</option>
                </select>
              </div>
              
              {/* Date Filter */}
              <div>
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5394] focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="year">Last Year</option>
                </select>
              </div>
            </div>
            
            {/* Clear Filters Button */}
            {(searchQuery || statusFilter !== 'all' || currencyFilter !== 'all' || dateFilter !== 'all') && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={clearFilters}
                  className="text-sm text-[#0B5394] hover:text-[#047857] font-medium transition-colors"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
          
          {filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">
                {invoices.length === 0 ? 'No invoices found' : 'No invoices match your filters'}
              </p>
              <p className="text-gray-500 text-sm mt-2">
                {invoices.length === 0 ? 'Create your first invoice to get started' : 'Try adjusting your search or filters'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Select All Row */}
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 flex items-center gap-3">
                <button
                  onClick={toggleSelectAll}
                  className="text-[#0B5394] hover:text-[#047857] transition-colors"
                >
                  {selectedInvoices.length === filteredInvoices.length ? (
                    <CheckSquare className="w-5 h-5" />
                  ) : (
                    <Square className="w-5 h-5" />
                  )}
                </button>
                <span className="text-sm text-gray-600 font-medium">
                  {selectedInvoices.length === filteredInvoices.length ? 'Deselect All' : 'Select All'}
                </span>
              </div>
              {filteredInvoices.map((invoice) => (
                <div key={invoice._id} className={`border-l-4 ${selectedInvoices.includes(invoice._id) ? 'border-[#047857] bg-green-50' : 'border-[#0B5394] bg-blue-50'} p-4 rounded hover:shadow-md transition-all`}>
                  <div className="flex justify-between items-start gap-4">
                    {/* Selection Checkbox */}
                    <button
                      onClick={() => toggleSelectInvoice(invoice._id)}
                      className="flex-shrink-0 mt-1 text-[#0B5394] hover:text-[#047857] transition-colors"
                    >
                      {selectedInvoices.includes(invoice._id) ? (
                        <CheckSquare className="w-5 h-5" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                    
                    {/* Logo Preview */}
                    <div className="flex-shrink-0">
                      {invoice.logoUrl ? (
                        <div className="w-20 h-20 rounded-lg overflow-hidden border-2 border-gray-300 bg-white shadow-sm">
                          <img 
                            src={invoice.logoUrl} 
                            alt="Company logo" 
                            className="w-full h-full object-contain p-1"
                          />
                        </div>
                      ) : (
                        <div className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 bg-white flex items-center justify-center">
                          <Building2 className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2">Invoice #{invoice.invoiceNumber}</h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600"><span className="font-medium">Client:</span> {invoice.clientName}</p>
                          <p className="text-gray-600"><span className="font-medium">Business:</span> {invoice.businessName}</p>
                        </div>
                        <div>
                          <p className="text-gray-600"><span className="font-medium">Date:</span> {invoice.invoiceDate}</p>
                          <p className="text-gray-600"><span className="font-medium">Items:</span> {invoice.items?.length || 0}</p>
                        </div>
                      </div>
                    </div>
                    <div className="text-right ml-6">
                      <p className="text-2xl font-bold text-[#047857] mb-2">
                        {getCurrencySymbol(invoice.currency)}{invoice.totalAmount.toFixed(2)}
                      </p>
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium capitalize mb-3 ${getStatusBadge(invoice.status)}`}>
                        {invoice.status.replace('_', ' ')}
                      </span>
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => handleDelete(invoice._id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete invoice"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
